import React from 'react';
import { Link } from 'react-router-dom';
import {
  SquaresFour, Briefcase, ChatCircleText, Calculator, UsersThree, Receipt,
  CalendarBlank, Gear, UserCircle, Gift, Wallet, Question, ShieldCheck,
  BookOpen, Translate, Clock, MapTrifold, Star, FileText, CalendarCheck, Warning
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_58284b88-53d8-4c2d-a757-f70994c6780a/artifacts/yxcl41jr_17fb61916fadfcb65ccf78419aa879e3f44d8d6b4289b01fdfd0f1c53d7537c0.png";

const CONTRACTOR_MENU = [
  {
    title: 'Workspace',
    links: [
      { to: '/contractor-dashboard', label: 'Dashboard', Icon: SquaresFour },
      { to: '/my-jobs', label: 'My Jobs', Icon: Briefcase },
      { to: '/post-job', label: 'Post a Job', Icon: FileText },
      { to: '/messages', label: 'Messages', Icon: ChatCircleText },
      { to: '/quotes', label: 'Sent Quotes', Icon: Receipt },
    ],
  },
  {
    title: 'Tools',
    links: [
      { to: '/ai-scheduler', label: 'AI Scheduler', Icon: CalendarCheck },
      { to: '/ai-pricing', label: 'AI Pricing Tool', Icon: Calculator },
      { to: '/find-workers', label: 'Find Workers', Icon: UsersThree },
      { to: '/leads-marketplace', label: 'Leads Marketplace', Icon: MapTrifold },
      { to: '/site-problems', label: 'Site Problems', Icon: Warning },
      { to: '/invoices', label: 'Invoices', Icon: Receipt },
      { to: '/calendar', label: 'Calendar', Icon: CalendarBlank },
    ],
  },
  {
    title: 'Account',
    links: [
      { to: '/contractor-control-panel', label: 'Control Panel', Icon: Gear },
      { to: '/verification', label: 'Verification', Icon: ShieldCheck },
      { to: '/subscription', label: 'Subscription', Icon: Star },
      { to: '/wallet', label: 'Wallet', Icon: Wallet },
      { to: '/referrals', label: 'Refer & Earn', Icon: Gift },
    ],
  },
];

const WORKER_MENU = [
  {
    title: 'Workspace',
    links: [
      { to: '/worker-dashboard', label: 'Dashboard', Icon: SquaresFour },
      { to: '/browse-jobs', label: 'Browse Jobs', Icon: Briefcase },
      { to: '/my-applications', label: 'My Applications', Icon: FileText },
      { to: '/messages', label: 'Messages', Icon: ChatCircleText },
      { to: '/saved-jobs', label: 'Saved Jobs', Icon: Star },
    ],
  },
  {
    title: 'Tools',
    links: [
      { to: '/my-availability', label: 'My Availability', Icon: CalendarBlank },
      { to: '/time-clock', label: 'Time Clock', Icon: Clock },
      { to: '/worker-teams', label: 'Worker Teams', Icon: UsersThree },
      { to: '/translator', label: 'Voice Translator', Icon: Translate },
      { to: '/site-problems', label: 'Site Problems', Icon: Warning },
      { to: '/job-alerts', label: 'Job Alerts', Icon: MapTrifold },
    ],
  },
  {
    title: 'Account',
    links: [
      { to: '/worker-control-panel', label: 'Control Panel', Icon: Gear },
      { to: '/verification', label: 'Verification', Icon: ShieldCheck },
      { to: '/wallet', label: 'Wallet', Icon: Wallet },
      { to: '/referrals', label: 'Refer & Earn', Icon: Gift },
      { to: '/timesheets', label: 'Timesheets', Icon: Clock },
    ],
  },
];

const HOMEOWNER_MENU = [
  {
    title: 'Workspace',
    links: [
      { to: '/homeowner-dashboard', label: 'Dashboard', Icon: SquaresFour },
      { to: '/my-jobs', label: 'My Projects', Icon: Briefcase },
      { to: '/post-job', label: 'Post a Job', Icon: FileText },
      { to: '/messages', label: 'Messages', Icon: ChatCircleText },
      { to: '/documents', label: 'Documents', Icon: FileText },
    ],
  },
  {
    title: 'Tools',
    links: [
      { to: '/find-workers', label: 'Find Workers', Icon: UsersThree },
      { to: '/escrow', label: 'Escrow Payments', Icon: Wallet },
      { to: '/project-planning', label: 'Project Planning', Icon: CalendarBlank },
      { to: '/favorite-workers', label: 'Favorite Pros', Icon: Star },
      { to: '/reports', label: 'Reports', Icon: MapTrifold },
    ],
  },
  {
    title: 'Account',
    links: [
      { to: '/account/subscription', label: 'Subscription', Icon: Star },
      { to: '/wallet', label: 'Wallet', Icon: Wallet },
      { to: '/referrals', label: 'Refer & Earn', Icon: Gift },
      { to: '/verification', label: 'Verification', Icon: ShieldCheck },
      { to: '/job-templates', label: 'Job Templates', Icon: Gear },
    ],
  },
];

const SUPPORT_LINKS = [
  { to: '/how-it-works', label: 'How It Works', Icon: Question },
  { to: '/guides', label: 'Cost Guides', Icon: BookOpen },
  { to: '/trust', label: 'Trust Center', Icon: ShieldCheck },
  { to: '/contact', label: 'Contact Us', Icon: ChatCircleText },
];

const MEMBER_MENUS = { contractor: CONTRACTOR_MENU, worker: WORKER_MENU, homeowner: HOMEOWNER_MENU };
export { MEMBER_MENUS, SUPPORT_LINKS };

export const MembersFooter = ({ role }) => {
  const menu = role === 'worker' ? WORKER_MENU : role === 'homeowner' ? HOMEOWNER_MENU : CONTRACTOR_MENU;
  const accent = role === 'worker' ? '#10B981' : role === 'homeowner' ? '#2563EB' : '#EA580C';

  return (
    <footer
      className="mt-12 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 pb-28 md:pb-10"
      data-testid="members-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-3">
              <img src={LOGO_URL} alt="ConstructHelp" className="h-12 w-auto" />
            </Link>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              Your {role === 'worker' ? 'work' : role === 'homeowner' ? 'project' : 'business'} hub — everything one click away.
            </p>
            <span
              className="inline-block px-3 py-1 rounded-full text-[11px] font-bold text-white"
              style={{ backgroundColor: accent }}
              data-testid="members-footer-role-chip"
            >
              {role === 'worker' ? 'WORKER MEMBER' : role === 'homeowner' ? 'HOMEOWNER MEMBER' : 'CONTRACTOR MEMBER'}
            </span>
          </div>

          {menu.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-3">{col.title}</h4>
              <ul className="space-y-2">
                {col.links.map(({ to, label, Icon }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      data-testid={`members-footer-link-${to.replace(/\//g, '')}`}
                    >
                      <Icon size={15} weight="bold" className="flex-shrink-0" />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-3">Support</h4>
            <ul className="space-y-2">
              {SUPPORT_LINKS.map(({ to, label, Icon }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    data-testid={`members-footer-link-${to.replace(/\//g, '')}`}
                  >
                    <Icon size={15} weight="bold" className="flex-shrink-0" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© {new Date().getFullYear()} ConstructHelp. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">Pricing</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MembersFooter;
